import React, { useState, useEffect, useCallback, useRef } from "react";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import useADUsers from "../../../hooks/useADUsers";

const MultiValueLabelWithTooltip = (props) => (
  <components.MultiValueLabel {...props}>
    <span title={props.data.label}>{props.children}</span>
  </components.MultiValueLabel>
);

const selectComponents = {
  MultiValueLabel: MultiValueLabelWithTooltip,
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    background: "#2e2e38",
    border: "1px solid #747480",
    borderRadius: "0px",
    minHeight: "38px",
    boxShadow: "none",
    flexWrap: "wrap",
    "&:hover": { borderColor: "#a4a3b1" },
  }),
  container: (provided) => ({
    ...provided,
    width: "100%",
  }),
  menu: (provided) => ({
    ...provided,
    background: "#2e2e38",
    border: "1px solid #747480",
    zIndex: 10,
  }),
  // Rendered into document.body via menuPortalTarget so the dropdown is never
  // clipped by the modal's scroll/overflow container. Keep it above the modal
  // overlay (z-index 1000).
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isFocused ? "#3a3a4a" : "transparent",
    color: "#ffffff",
    cursor: "pointer",
    "&:active": { background: "#3a3a4a" },
  }),
  multiValue: (provided) => ({
    ...provided,
    background: "#3a3a4a",
    borderRadius: "12px",
    border: "1px solid #747480",
    maxWidth: "200px",
    margin: "2px 4px 2px 0",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#ffffff",
    fontFamily: "EYInterstate",
    fontWeight: 300,
    fontSize: "13px",
    padding: "3px 6px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#a4a3b1",
    borderRadius: "0 12px 12px 0",
    cursor: "pointer",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
    "&:hover": { background: "#b9251c", color: "#ffffff" },
    "& svg": { width: "14px", height: "14px" },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "4px 8px",
    gap: "2px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#ffffff",
    fontFamily: "EYInterstate",
    fontWeight: 300,
    fontSize: "14px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#747480",
    fontFamily: "EYInterstate",
    fontWeight: 300,
    fontSize: "14px",
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    color: "#a4a3b1",
    fontFamily: "EYInterstate",
    fontSize: "13px",
  }),
  loadingMessage: (provided) => ({
    ...provided,
    color: "#a4a3b1",
  }),
};

function toSelectOptions(delegates) {
  return (delegates || [])
    .filter((d) => d.delegateEmail || d.emailAddress)
    .map((d) => ({
      label: d.delegateEmail || d.emailAddress,
      value: d.delegateEmail || d.emailAddress,
      firstName: d.firstName || "",
      lastName: d.lastName || "",
    }));
}

/**
 * Reusable editable delegate picker with AD user search and manual email entry.
 *
 * @param {object[]} delegates       - Array of delegate objects ({ delegateEmail } or { emailAddress })
 * @param {function} [onChange]      - Called with an array of email strings whenever selection changes
 * @param {string}   [placeholder]   - Input placeholder text
 * @param {boolean}  [disabled]      - Disable the entire field
 * @param {number}   [maxItems]      - Maximum number of delegates allowed (0 = no limit)
 * @param {string}   [limitReachedMessage] - Message shown in the dropdown when limit is reached
 */
export function EditableDelegateField({
  delegates,
  onChange,
  placeholder = "Enter email to add delegate..",
  disabled = false,
  maxItems = 0,
  limitReachedMessage,
  menuPlacement = "auto",
}) {
  const { getADUsers } = useADUsers();
  const nameMapRef = useRef({});
  const containerRef = useRef(null);
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);

  // Portal the menu into the nearest modal element (inside its focus trap) when
  // rendered within a MotifModal; otherwise fall back to document.body. This is
  // required because MotifModal traps focus via focus-trap-react: a menu
  // portaled to document.body sits outside the trap, so clicking an option
  // pulls focus back into the modal and the selection never registers.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const modalEl = containerRef.current?.closest(".motif-modal");
    setMenuPortalTarget(modalEl || document.body);
  }, []);

  const [selectedOptions, setSelectedOptions] = useState(() => {
    const opts = toSelectOptions(delegates);
    opts.forEach((o) => {
      if (o.firstName || o.lastName) {
        nameMapRef.current[o.value] = {
          firstName: o.firstName,
          lastName: o.lastName,
        };
      }
    });
    return opts;
  });

  useEffect(() => {
    const opts = toSelectOptions(delegates);
    opts.forEach((o) => {
      if (o.firstName || o.lastName) {
        nameMapRef.current[o.value] = {
          firstName: o.firstName,
          lastName: o.lastName,
        };
      }
    });
    setSelectedOptions(opts);
  }, [delegates]);

  const loadOptions = useCallback(
    async (inputValue, callback) => {
      if (!inputValue || inputValue.length < 2) {
        callback([]);
        return;
      }
      try {
        const data = await getADUsers(inputValue);
        if (Array.isArray(data)) {
          const options = data
            .filter((item) => item.emailId)
            .map((item) => {
              const firstName = item.firstName || "";
              const lastName = item.lastName || "";
              if (firstName || lastName) {
                nameMapRef.current[item.emailId] = { firstName, lastName };
              }
              return {
                label: item.emailId,
                value: item.emailId,
                displayName: item.displayName,
                firstName,
                lastName,
              };
            });
          callback(options);
        } else {
          callback([]);
        }
      } catch {
        callback([]);
      }
    },
    [getADUsers],
  );

  const formatOptionLabel = useCallback((option, { context }) => {
    if (context === "menu" && option.displayName) {
      return (
        <div>
          <div style={{ fontWeight: 400 }}>{option.displayName}</div>
          <div style={{ fontSize: "12px", color: "#a4a3b1" }}>
            {option.label}
          </div>
        </div>
      );
    }
    return option.label;
  }, []);

  const isAtLimit = maxItems > 0 && selectedOptions.length >= maxItems;
  const defaultLimitMessage =
    limitReachedMessage ||
    `You can add a maximum of ${maxItems} delegate${maxItems === 1 ? "" : "s"}.`;

  const handleChange = (newValue) => {
    let opts = newValue || [];
    if (maxItems > 0 && opts.length > maxItems) {
      opts = opts.slice(0, maxItems);
    }
    setSelectedOptions(opts);
    if (onChange) {
      onChange(
        opts.map((opt) => {
          const cached = nameMapRef.current[opt.value] || {};
          return {
            emailAddress: opt.value,
            firstName: opt.firstName || cached.firstName || "",
            lastName: opt.lastName || cached.lastName || "",
          };
        }),
      );
    }
  };

  return (
    <div className="editable-delegate-field" ref={containerRef}>
      <div className="delegate-picker-container">
        <AsyncSelect
          isMulti
          cacheOptions
          isDisabled={disabled}
          loadOptions={isAtLimit ? (_, cb) => cb([]) : loadOptions}
          value={selectedOptions}
          onChange={handleChange}
          placeholder={isAtLimit ? defaultLimitMessage : placeholder}
          formatOptionLabel={formatOptionLabel}
          noOptionsMessage={({ inputValue }) => {
            if (isAtLimit) return defaultLimitMessage;
            return inputValue.length < 2
              ? "Type to search..."
              : "No users found";
          }}
          closeMenuOnSelect={false}
          menuPlacement={menuPlacement}
          menuPosition="fixed"
          menuPortalTarget={menuPortalTarget || undefined}
          styles={selectStyles}
          components={selectComponents}
        />
      </div>
    </div>
  );
}
